import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitBranch, Loader2, Plus, Save, Trash2, X, Pencil, RefreshCw, FlaskConical, Check, AlertTriangle, Building2 } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium";
const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";
const HEATING_OPTIONS = ["gas","oel","pellet","holz","fernwaerme","waermepumpe","strom","sonstige"];
const PACKAGE_OPTIONS = ["guenstig","komfort","premium"];
const APPT_OPTIONS = ["vor_ort","telefon"];

const emptyDraft = () => ({
  name: "", priority: 100, enabled: true,
  conditions: { plz_prefixes: [], package: [], current_heating: [], building_type: [], appointment_type: [] },
  distribution_mode: "auction", assign_to_team_id: "", fixed_assignment_strategy: null, tenant_id: "",
});

const ChipPicker = ({ value = [], options, onChange, testid }) => (
  <div className="flex flex-wrap gap-1" data-testid={testid}>
    {options.map(o => (
      <button key={o} type="button" onClick={() => onChange(value.includes(o) ? value.filter(x => x !== o) : [...value, o])}
        className={`px-2 py-1 rounded text-[12px] font-bold transition-colors ${value.includes(o) ? "bg-[#00E575]/15 text-[#00E575]" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}>
        {o}
      </button>
    ))}
  </div>
);

export default function AdminRoutingRules() {
  const { user: currentUser } = useAuth();
  const [rules, setRules] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dryRun, setDryRun] = useState({ open: false, payload: {}, result: null });
  const [tenantOptions, setTenantOptions] = useState([]);

  // Super-Admin in Global Mode → braucht inline Tenant-Auswahl
  const isSuperAdmin = !!currentUser?.is_super_admin;
  const actingTenantId = (typeof window !== "undefined")
    ? localStorage.getItem("novatus_acting_tenant_id") || ""
    : "";
  const needsTenantPick = isSuperAdmin && !actingTenantId;

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: t }] = await Promise.all([
        api.get("/admin/routing-rules?show_disabled=true"),
        api.get("/admin/sales-teams"),
      ]);
      setRules(r || []); setTeams(t || []);
    } catch (e) {
      if (needsTenantPick && e.response?.status === 400) {
        setRules([]); setTeams([]);
      } else {
        toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
      }
    }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Tenant-Liste nur einmal laden, wenn Super-Admin in Global Mode ist
  useEffect(() => {
    if (!needsTenantPick) return;
    api.get("/superadmin/tenants")
      .then((r) => setTenantOptions((r.data || []).filter((t) => t.active !== false)))
      .catch(() => setTenantOptions([]));
  }, [needsTenantPick]);

  const openNew = () => setEditor({ isNew: true, ...emptyDraft() });
  const openEdit = (r) => setEditor({ isNew: false, ...r });

  const save = async () => {
    if (!editor.name) return toast.error("Name ist Pflicht");
    if (!editor.assign_to_team_id) return toast.error("Team ist Pflicht");
    if (needsTenantPick && editor.isNew && !editor.tenant_id) {
      return toast.error("Bitte einen Mandanten auswählen (Sie sind im Global-Modus).");
    }
    setSaving(true);
    try {
      const payload = { ...editor };
      delete payload.isNew; delete payload.id;
      delete payload.created_at; delete payload.updated_at;
      // Nur im Global-Modus beim Neu-Anlegen tenant_id mitsenden
      if (!needsTenantPick || !editor.isNew) delete payload.tenant_id;
      // Convert empty arrays to null so they don't restrict matching
      Object.keys(payload.conditions).forEach(k => {
        if (Array.isArray(payload.conditions[k]) && payload.conditions[k].length === 0) {
          payload.conditions[k] = null;
        }
      });
      if (editor.isNew) await api.post("/admin/routing-rules", payload);
      else await api.patch(`/admin/routing-rules/${editor.id}`, payload);
      toast.success(editor.isNew ? "Regel angelegt" : "Gespeichert");
      setEditor(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Regel "${r.name}" löschen?`)) return;
    await api.delete(`/admin/routing-rules/${r.id}`);
    toast.success("Gelöscht"); await load();
  };

  const runDryRun = async () => {
    try {
      const { data } = await api.post("/admin/routing-rules/test", dryRun.payload);
      setDryRun(s => ({ ...s, result: data }));
    } catch (e) { toast.error(e.response?.data?.detail || "Dry-Run fehlgeschlagen"); }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-routing-rules">
      <AppHeader title="Routing-Regeln" back="/dashboard" />
      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <GitBranch className="text-[#00E575]" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">Routing-Regeln</h1>
              <p className="text-[12px] text-zinc-300 font-medium">Bestimmen, wo neue Leads landen (Auktion oder feste Zuweisung)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setDryRun({ open: true, payload: {}, result: null })} data-testid="dry-run-open" className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-800 hover:border-amber-500/40 text-[12px] inline-flex items-center gap-1.5 font-medium"><FlaskConical size={13}/>Dry-Run</button>
            <button onClick={load} data-testid="rules-reload" className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/40 text-[12px] inline-flex items-center gap-1.5 font-medium"><RefreshCw size={13}/>Neu laden</button>
            <button onClick={openNew} data-testid="rules-new" className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964]"><Plus size={14}/>Neue Regel</button>
          </div>
        </div>

        {loading && <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 flex justify-center"><Loader2 className="animate-spin text-zinc-400"/></div>}

        {!loading && rules.length === 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">Noch keine Routing-Regeln. Leads bleiben ohne Zuweisung — klick auf <b className="text-[#00E575]">Neue Regel</b>.</div>
        )}

        {!loading && rules.map(r => {
          const team = teams.find(t => t.id === r.assign_to_team_id);
          const condEntries = Object.entries(r.conditions || {}).filter(([_, v]) => Array.isArray(v) && v.length > 0);
          return (
            <section key={r.id} className={`bg-[#18181B] border rounded-2xl p-4 ${r.enabled ? "border-zinc-800" : "border-zinc-800 opacity-60"}`} data-testid={`rule-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="bg-zinc-800 px-2 py-0.5 rounded text-[12px] font-bold text-zinc-300">#{r.priority}</span>
                    <h2 className="font-bold text-[14px]">{r.name}</h2>
                    {!r.enabled && <span className="text-[12px] uppercase tracking-wider bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded">deaktiviert</span>}
                  </div>
                  <div className="mt-2 text-[11.5px] text-zinc-400 flex flex-wrap gap-x-3 gap-y-1">
                    {condEntries.length === 0 && <span>📥 Catch-All (keine Bedingung)</span>}
                    {condEntries.map(([k, v]) => <span key={k}>{k}: <b className="text-zinc-200">{v.join(", ")}</b></span>)}
                  </div>
                  <div className="mt-2 text-[11.5px]">
                    <span className={`font-bold ${r.distribution_mode === "auction" ? "text-[#00E575]" : "text-amber-400"}`}>{r.distribution_mode === "auction" ? "→ Auktion" : "→ Feste Zuweisung"}</span>
                    <span className="text-zinc-400"> · Team: <b className="text-zinc-200">{team?.name || "—"}</b></span>
                    {r.distribution_mode === "fixed" && r.fixed_assignment_strategy && <span className="text-zinc-500"> · {r.fixed_assignment_strategy}</span>}
                    {team?.type === "external" && <span className="text-amber-400 ml-2">⚠ extern → erzwungen fest</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(r)} data-testid={`rule-edit-${r.id}`} className="h-8 w-8 rounded bg-[#09090B] border border-zinc-700 hover:border-[#00E575]/50 inline-flex items-center justify-center"><Pencil size={12}/></button>
                  <button onClick={() => remove(r)} data-testid={`rule-del-${r.id}`} className="h-8 w-8 rounded bg-[#09090B] border border-zinc-700 hover:border-red-500/50 hover:text-red-400 inline-flex items-center justify-center"><Trash2 size={12}/></button>
                </div>
              </div>
            </section>
          );
        })}
      </main>

      {editor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={() => !saving && setEditor(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-[#18181B] border-b border-zinc-800 px-5 py-4 z-10">
              <h2 className="font-display text-lg font-bold">{editor.isNew ? "Neue Regel" : "Regel bearbeiten"}</h2>
              <button onClick={() => setEditor(null)} className="h-8 w-8 rounded bg-zinc-800 hover:bg-zinc-700"><X size={14}/></button>
            </div>
            <div className="p-5 space-y-4">
              {needsTenantPick && editor.isNew && (
                <div data-testid="rule-tenant-wrapper" className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
                  <label className="text-[12px] uppercase tracking-widest text-amber-300 mb-1 font-bold flex items-center gap-1.5">
                    <Building2 size={12} /> Mandant *
                  </label>
                  <select
                    value={editor.tenant_id}
                    onChange={(e) => setEditor({ ...editor, tenant_id: e.target.value })}
                    data-testid="rule-edit-tenant"
                    className="h-10 w-full bg-[#09090B] border border-amber-500/40 rounded-lg px-3 text-zinc-50 focus:border-amber-400 outline-none text-[13px]"
                  >
                    <option value="">— Mandant auswählen —</option>
                    {tenantOptions.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                    ))}
                  </select>
                  <p className="text-[10.5px] text-amber-200/80 mt-1 leading-snug">
                    Sie sind im <strong>Global-Modus</strong>. Wählen Sie den Mandanten, für den diese Regel gelten soll.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><label className={labelCls}>Name *</label><input value={editor.name} onChange={e => setEditor({ ...editor, name: e.target.value })} data-testid="rule-edit-name" className={inputCls} /></div>
                <div><label className={labelCls}>Priorität (klein = zuerst)</label><input type="number" value={editor.priority} onChange={e => setEditor({ ...editor, priority: Number(e.target.value) })} className={inputCls} /></div>
              </div>
              <fieldset className="bg-[#09090B] border border-zinc-800 rounded-xl p-3 space-y-3">
                <legend className="text-[12px] uppercase tracking-widest text-[#00E575] font-bold px-1">Bedingungen (AND, leer = jeder)</legend>
                <div>
                  <label className={labelCls}>PLZ-Präfixe (Komma-separiert)</label>
                  <input value={(editor.conditions.plz_prefixes || []).join(", ")} onChange={e => setEditor({ ...editor, conditions: { ...editor.conditions, plz_prefixes: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }})} placeholder="z.B. 10, 11, 12" className={inputCls} />
                </div>
                <div><label className={labelCls}>Paket</label><ChipPicker testid="rule-cond-package" value={editor.conditions.package || []} options={PACKAGE_OPTIONS} onChange={v => setEditor({ ...editor, conditions: { ...editor.conditions, package: v }})}/></div>
                <div><label className={labelCls}>Heizung</label><ChipPicker testid="rule-cond-heating" value={editor.conditions.current_heating || []} options={HEATING_OPTIONS} onChange={v => setEditor({ ...editor, conditions: { ...editor.conditions, current_heating: v }})}/></div>
                <div><label className={labelCls}>Terminart</label><ChipPicker value={editor.conditions.appointment_type || []} options={APPT_OPTIONS} onChange={v => setEditor({ ...editor, conditions: { ...editor.conditions, appointment_type: v }})}/></div>
              </fieldset>
              <fieldset className="bg-[#09090B] border border-zinc-800 rounded-xl p-3 space-y-3">
                <legend className="text-[12px] uppercase tracking-widest text-[#00E575] font-bold px-1">Aktion</legend>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Modus *</label>
                    <select value={editor.distribution_mode} onChange={e => setEditor({ ...editor, distribution_mode: e.target.value })} data-testid="rule-edit-mode" className={inputCls}>
                      <option value="auction">Auktion</option>
                      <option value="fixed">Feste Zuweisung</option>
                    </select>
                  </div>
                  <div><label className={labelCls}>Team *</label>
                    <select value={editor.assign_to_team_id} onChange={e => setEditor({ ...editor, assign_to_team_id: e.target.value })} data-testid="rule-edit-team" className={inputCls}>
                      <option value="">— wählen —</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name} ({t.type})</option>)}
                    </select>
                  </div>
                </div>
                {editor.distribution_mode === "fixed" && (
                  <div><label className={labelCls}>Verteilstrategie (optional, sonst Team-Default)</label>
                    <select value={editor.fixed_assignment_strategy || ""} onChange={e => setEditor({ ...editor, fixed_assignment_strategy: e.target.value || null })} className={inputCls}>
                      <option value="">— Team-Default —</option>
                      <option value="round_robin">Round-Robin</option>
                      <option value="first_available">First Available</option>
                      <option value="least_loaded">Least Loaded</option>
                      <option value="team_inbox">Team-Inbox</option>
                    </select>
                  </div>
                )}
              </fieldset>
              <label className="inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={editor.enabled} onChange={e => setEditor({ ...editor, enabled: e.target.checked })} className="accent-[#00E575]"/> Regel aktiv</label>
            </div>
            <div className="sticky bottom-0 bg-[#18181B] border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setEditor(null)} className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-medium">Abbrechen</button>
              <button onClick={save} disabled={saving} data-testid="rule-save" className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">{saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>}{editor.isNew ? "Anlegen" : "Speichern"}</button>
            </div>
          </div>
        </div>
      )}

      {dryRun.open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={() => setDryRun({ open: false, payload: {}, result: null })}>
          <div onClick={e => e.stopPropagation()} className="bg-[#18181B] border border-amber-500/40 rounded-2xl max-w-lg w-full">
            <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
              <h2 className="font-bold inline-flex items-center gap-2"><FlaskConical className="text-amber-400" size={16}/> Routing Dry-Run</h2>
              <button onClick={() => setDryRun({ open: false, payload: {}, result: null })} className="h-8 w-8 rounded bg-zinc-800"><X size={14}/></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>PLZ</label><input value={dryRun.payload.zip || ""} onChange={e => setDryRun(s => ({...s, payload: { ...s.payload, zip: e.target.value }, result: null }))} data-testid="dry-run-zip" className={inputCls} placeholder="10115"/></div>
                <div><label className={labelCls}>Paket</label>
                  <select value={dryRun.payload.package || ""} onChange={e => setDryRun(s => ({...s, payload: { ...s.payload, package: e.target.value }, result: null }))} className={inputCls}>
                    <option value="">—</option>{PACKAGE_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Heizung</label>
                  <select value={dryRun.payload.current_heating || ""} onChange={e => setDryRun(s => ({...s, payload: { ...s.payload, current_heating: e.target.value }, result: null }))} className={inputCls}>
                    <option value="">—</option>{HEATING_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Terminart</label>
                  <select value={dryRun.payload.appointment_type || ""} onChange={e => setDryRun(s => ({...s, payload: { ...s.payload, appointment_type: e.target.value }, result: null }))} className={inputCls}>
                    <option value="">—</option>{APPT_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={runDryRun} data-testid="dry-run-execute" className="w-full h-10 rounded-lg bg-amber-500 text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-amber-400"><FlaskConical size={14}/> Dry-Run starten</button>
              {dryRun.result && (
                <div className={`mt-3 p-3 rounded-lg border ${dryRun.result.matched ? "bg-[#00E575]/5 border-[#00E575]/40" : "bg-amber-500/5 border-amber-500/40"}`} data-testid="dry-run-result">
                  {dryRun.result.matched ? (
                    <>
                      <div className="flex items-center gap-2 mb-2"><Check className="text-[#00E575]" size={16}/><b>Regel matched</b></div>
                      <div className="text-[12px] space-y-1 font-medium">
                        <div>Regel: <b>{dryRun.result.rule.name}</b> (Priorität #{dryRun.result.rule.priority})</div>
                        <div>Team: <b>{dryRun.result.team.name}</b> ({dryRun.result.team.type})</div>
                        <div>Modus: <b className={dryRun.result.distribution_mode === "auction" ? "text-[#00E575]" : "text-amber-400"}>{dryRun.result.distribution_mode}</b></div>
                        {dryRun.result.fixed_assignment_strategy && <div>Strategie: {dryRun.result.fixed_assignment_strategy}</div>}
                        <div className="text-zinc-500 mt-2">Warum: {dryRun.result.why.join(" · ")}</div>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-400"><AlertTriangle size={16}/><b>Keine Regel matched</b></div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
